import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { FIRM_CERTIFICATE_ATTENTION_FILTERS, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { IFirm } from 'app/models/firms/firm';
import { IFirmFilter } from 'app/models/firms/firm-filter';
import { CommonService } from 'app/services/common/common.service';
import { SharedService } from 'app/services/core/shared.service';
import { FirmService } from 'app/services/firms/firm.service';
import { ToastrService } from 'ngx-toastr';
import { FirmEditComponent } from '../firm-edit/firm-edit.component';

@Component({
  selector: 'app-firm-management',
  templateUrl: './firm-management.component.html',
  styleUrls: ['./firm-management.component.scss']
})
export class FirmManagementComponent implements OnInit {
  public isMobile = false;
  public loading = true;
  public pageSize = 25;
  public uiData = LEAD_BASED_PAINT;
  public filterString = '';
  public filteredActive = new FormControl('Both');
  public expirationStartDate = new FormControl(null);
  public expirationEndDate = new FormControl(null);
  public selectedCertificateStatuses = new FormControl([]);
  public firms: IFirm[] = [];
  public dataSource = new MatTableDataSource<IFirm>([]);
  public displayedColumns = ['name', 'status', 'visibility', 'services', 'ccb', 'expiration', 'certificates', 'actions'];
  public firmTable = {
    layout: {
      columns: ['name', 'status', 'visibility', 'services', 'ccb', 'expiration', 'certificates'],
      container: [
        { displayName: 'Name', columnName: 'name', type: 'string', size: '20' },
        { displayName: 'Status', columnName: 'status', type: 'status', size: '9' },
        { displayName: 'Visibility', columnName: 'visibility', type: 'string', size: '10' },
        { displayName: 'Services', columnName: 'services', type: 'string', size: '17' },
        { displayName: 'CCB / PML', columnName: 'ccb', type: 'string', size: '12' },
        { displayName: 'Primary Expiration', columnName: 'expiration', type: 'date', size: '12' },
        { displayName: 'Certificates', columnName: 'certificates', type: 'string', size: '12' },
      ],
      actionColumnSize: '8',
    },
  };

  @ViewChild('TablePaginator') paginator!: MatPaginator;
  @ViewChild('TableSort') sort!: MatSort;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private router: Router,
    private commonService: CommonService,
    private firmService: FirmService,
    public sharedService: SharedService,
    private toastr: ToastrService,
  ) {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe((result) => {
      this.isMobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.loadFilters();
    this.loadFirms();
  }

  loadFilters(): void {
    this.filterString = this.commonService.firmManagementFilter.filterString;
    this.filteredActive = this.commonService.firmManagementFilter.filteredActive;
    this.expirationStartDate = this.commonService.firmManagementFilter.expirationStartDate;
    this.expirationEndDate = this.commonService.firmManagementFilter.expirationEndDate;
    this.selectedCertificateStatuses = this.commonService.firmManagementFilter.selectedCertificateStatuses;
  }

  loadFirms(): void {
    this.loading = true;
    this.persistFilters();

    this.firmService.getUserFirms(this.getFilter()).subscribe({
      next: (firms) => {
        this.firms = firms;
        this.dataSource = new MatTableDataSource<IFirm>(firms);
        setTimeout(() => {
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }
        });
        this.loading = false;
      },
      error: (error) => {
        this.toastr.error('Error loading Firms: ' + error.message);
        this.loading = false;
      }
    });
  }

  clearFilters(): void {
    this.filterString = '';
    this.filteredActive.setValue('Both');
    this.expirationStartDate.reset();
    this.expirationEndDate.reset();
    this.selectedCertificateStatuses.setValue([]);
    this.loadFirms();
  }

  openFirmEdit(firm: IFirm): void {
    const dialogRef = this.dialog.open(FirmEditComponent, {
      width: '95%',
      maxWidth: '1400px',
      height: this.isMobile ? '95%' : '94vh',
      autoFocus: false,
      data: { firmId: firm.id },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadFirms();
      }
    });
  }

  openApplications(): void {
    this.router.navigate(['/firms/applications']);
  }

  getCertificateAttentionFilterValues(): string[] {
    return Object.values(FIRM_CERTIFICATE_ATTENTION_FILTERS);
  }

  getStatus(firm: IFirm): string {
    return firm.inactive ? 'Inactive' : 'Active';
  }

  getVisibility(firm: IFirm): string {
    return firm.isPrivate ? 'Private' : 'Public';
  }

  getCertificateSummary(firm: IFirm): string {
    const summary = firm.certificateSummary;
    if (!summary) {
      return '0 active / 0 expiring / 0 expired';
    }

    return summary.active + ' active / ' + summary.expiring + ' expiring / ' + summary.expired + ' expired';
  }

  getColumnText(firm: IFirm, columnName: string): string {
    switch (columnName) {
      case 'status':
        return this.getStatus(firm);
      case 'visibility':
        return this.getVisibility(firm);
      case 'services':
        return firm.servicesAvailable || 'Not provided';
      case 'ccb':
        return firm.ccb || firm.propertyManagementLicense || 'Not provided';
      case 'certificates':
        return this.getCertificateSummary(firm);
      case 'expiration':
        return firm.nextCertificateExpirationDate || firm.ccbExpirationDate || '';
      default: {
        const value = firm[columnName as keyof IFirm];
        return value === null || value === undefined || value === '' ? 'Not provided' : value.toString();
      }
    }
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  private persistFilters(): void {
    this.commonService.firmManagementFilter.filterString = this.filterString;
    this.commonService.firmManagementFilter.filteredActive = this.filteredActive;
    this.commonService.firmManagementFilter.expirationStartDate = this.expirationStartDate;
    this.commonService.firmManagementFilter.expirationEndDate = this.expirationEndDate;
    this.commonService.firmManagementFilter.selectedCertificateStatuses = this.selectedCertificateStatuses;
  }

  private getFilter(): IFirmFilter {
    return {
      filterString: this.filterString,
      filterStatus: this.filteredActive.value,
      expirationStartDate: this.expirationStartDate.value ? new Date(this.expirationStartDate.value).toISOString() : null,
      expirationEndDate: this.expirationEndDate.value ? new Date(this.expirationEndDate.value).toISOString() : null,
      certificateStatuses: this.selectedCertificateStatuses.value || [],
    };
  }
}